import {
  call,
  select,
  take,
  fork,
  put,
  cancel,
  cancelled,
} from 'redux-saga/effects';
import { delay } from 'redux-saga';
import { REHYDRATE } from 'redux-persist/constants';
import moment from 'moment-timezone';

import { bindAsyncAction } from '../../utils/sagas';
import {
  fetchDevices,
  fetchDevicesStats,
  lockDoors,
  toggleIgnition,
  toggleServiceMode,
  toggleTrunk,
  changeCurrentDeviceState,
  toggleWebasto,
  setAutorunEngineDelaySeconds,
  clearTooltip,
  setTooltip,
  setWebastoPeriodSeconds,
} from './actions';
import { logoutButtonClick } from '../../components/Auth/actions';
import {
  lockDoorsClick,
  ignitionClick,
  serviceModeClick,
  trunkClick,
  setWebasto,
  setAutorunEngineDelayClick,
  setWebastoPeriodClick,
} from '../../components/Sidebar/actions';
import { changeCurrentDevice } from '../../components/Header/actions';
import { login } from '../auth/actions';
import { apiRequest, geoCodeRequest } from '../../utils/api';

const worker = bindAsyncAction(fetchDevices, function* getDevices(params) {
  return yield call(apiRequest, params);
});
const getStatsWorker = bindAsyncAction(
  fetchDevicesStats,
  function* getDevicesStats(params) {
    let { data } = yield call(apiRequest, params);

    try {
      const geoResponse = yield call(geoCodeRequest, {
        body: JSON.stringify({
          requests: data.map(item => ({
            Latitude: item.lat / 10000000,
            Longitude: item.lon / 10000000,
          })),
        }),
      });
      data = data.map((item, index) => {
        item.geoData = geoResponse[index];
        return item;
      });
    } catch (e) {
      return { data };
    }
    return { data }; // eslint-disable-line consistent-return
  },
);
const lockDoorsWorker = bindAsyncAction(lockDoors, function* postLockDoors(
  params,
) {
  return yield call(apiRequest, params);
});
const toggleIgnitionWorker = bindAsyncAction(
  toggleIgnition,
  function* postToggleIgnition(params) {
    return yield call(apiRequest, params);
  },
);
const toggleServiceModeWorker = bindAsyncAction(
  toggleServiceMode,
  function* postToggleServiceMode(params) {
    return yield call(apiRequest, params);
  },
);
const toggleTrunkWorker = bindAsyncAction(
  toggleTrunk,
  function* postToggleTrunk(params) {
    return yield call(apiRequest, params);
  },
);
const setWebastoWorker = bindAsyncAction(toggleWebasto, function* postWebasto(
  params,
) {
  return yield call(apiRequest, params);
});
const setAutorunEngineDelayInSecondsWorker = bindAsyncAction(
  setAutorunEngineDelaySeconds,
  function* postSeconds(params) {
    return yield call(apiRequest, params);
  },
);
const setWebastoPeriodInSecondsWorker = bindAsyncAction(
  setWebastoPeriodSeconds,
  function* postSeconds(params) {
    return yield call(apiRequest, params);
  },
);
export default function* devicesSaga() {
  while (true) {
    yield take(login.done.type);
    try {
      yield call(worker, {
        url: '/api/v3/Devices',
      });
    } catch (e) {
      return;
    }

    const setMomentOffset = yield fork(setMomentOffsetSaga);
    const devicesAction = yield fork(devicesActionsSaga);
    const changeDevice = yield fork(changeDeviceStateSaga);

    yield take(logoutButtonClick.type);
    yield cancel(setMomentOffset);
    yield cancel(devicesAction);
    yield cancel(changeDevice);
  }
}

function* setMomentOffsetSaga() {
  while (true) {
    const { utcOffsetInMinutes } = yield select(
      state => state.devices.currentDevice,
    );
    if (typeof utcOffsetInMinutes === 'number') {
      moment.tz.setDefault(
        `Etc/GMT${utcOffsetInMinutes < 0 ? '+' : '-'}${Math.abs(
          (utcOffsetInMinutes / 60) ^ 0,
        )}`,
      );
    }
    yield take([changeCurrentDevice.type, fetchDevices.done.type, REHYDRATE]);
  }
}

function* changeDeviceStateSaga() {
  while (true) {
    const { payload } = yield take(changeCurrentDevice.type);

    const states = yield select(state => state.devices.devicesStats);
    yield put({
      type: changeCurrentDeviceState.type,
      payload: states.filter(s => s.deviceId === payload.device.id).pop(),
    });
  }
}

function* devicesActionsSaga() {
  const devices = yield select(state => state.devices.devices);
  if (devices.length) {
    yield fork(lockDoorsSaga);
    yield fork(ignitionSaga);
    yield fork(serviceModeSaga);
    yield fork(trunkSaga);
    yield fork(autorunSaga);
    yield fork(webastoSaga);
    yield fork(autorunGetStatsSaga);
    yield fork(webastoPeriodSaga);
    while (true) {
      yield call(getStatsSaga);

      yield delay(7 * 1000);
    }
  }
}
function* autorunGetStatsSaga() {
  while (true) {
    const action = yield take(changeCurrentDevice.type);
    const devicesIds = yield select(state =>
      state.devices.devices.map(d => d.id),
    );
    try {
      yield call(getStatsWorker, {
        currentDeviceId: action.payload.device.id,
        url: '/api/v3/DevicesStates',
        params: {
          Devices: devicesIds,
        },
      });
    } catch (e) {}
  }
}
function* getStatsSaga() {
  const devicesIds = yield select(state =>
    state.devices.devices.map(d => d.id),
  );
  const currentDeviceId = yield select(state => state.devices.currentDevice.id);

  try {
    yield call(getStatsWorker, {
      currentDeviceId,
      url: '/api/v3/DevicesStates',
      params: {
        Devices: devicesIds,
      },
    });
  } catch (e) {}
}

function* lockDoorsSaga() {
  // eslint-disable-line no-unused-vars
  while (!(yield cancelled())) {
    const { payload } = yield take(lockDoorsClick.type);

    yield put({
      type: setTooltip.type,
      payload: { type: lockDoors.started.type, on: payload.on },
    });

    try {
      yield call(lockDoorsWorker, {
        deviceId: payload.deviceId,
        url: `/api/v3/Devices/${payload.deviceId}/LockDoor`,
        body: JSON.stringify(!payload.on),

        method: 'POST',
      });
    } catch (e) {
      yield put({
        type: setTooltip.type,
        payload: { type: lockDoors.failed.type, on: payload.on },
      });
    }
  }
}

function* ignitionSaga() {
  // eslint-disable-line no-unused-vars
  while (true) {
    const { payload } = yield take(ignitionClick.type);
    try {
      yield call(toggleIgnitionWorker, {
        deviceId: payload.deviceId,
        url: `/api/v3/Devices/${payload.deviceId}/Run`,
        method: 'POST',
        body: JSON.stringify({ on: !payload.on }),
      });
    } catch (e) {}
  }
}

function* serviceModeSaga() {
  // eslint-disable-line no-unused-vars
  while (true) {
    const { payload } = yield take(serviceModeClick.type);

    yield put({
      type: setTooltip.type,
      payload: { type: toggleServiceMode.started.type, on: payload.on },
    });

    try {
      yield call(toggleServiceModeWorker, {
        url: `/api/v3/Devices/${payload.deviceId}/SetServiceMode`,
        method: 'POST',
        body: JSON.stringify(payload.on),
      });
      yield put({
        type: setTooltip.type,
        payload: { type: toggleServiceMode.done.type, on: payload.on },
      });
      yield delay(7000);
      yield put({ type: clearTooltip.type });
    } catch (e) {
      yield put({
        type: setTooltip.type,
        payload: { type: toggleServiceMode.failed.type, on: payload.on },
      });
      yield delay(7000);
      yield put({ type: clearTooltip.type });
    }
  }
}

function* trunkSaga() {
  // eslint-disable-line no-unused-vars
  while (true) {
    const { payload } = yield take(trunkClick.type);

    try {
      yield call(toggleTrunkWorker, {
        deviceId: payload.deviceId,
        url: `/api/v3/Devices/${payload.deviceId}/LockTrunk`,
        method: 'POST',
        body: JSON.stringify(payload.on),
      });
    } catch (e) {}
  }
}

function* autorunSaga() {
  while (true) {
    const { payload } = yield take(setAutorunEngineDelayClick.type);

    try {
      yield call(setAutorunEngineDelayInSecondsWorker, {
        method: 'POST',
        url: `/api/v3/Devices/${payload.deviceId}/autorunEngineDelayInSeconds`,
        body: JSON.stringify({
          delay: payload.seconds,
        }),
      });
    } catch (e) {
      void 0;
    }

    yield delay(1000);
  }
}

function* webastoSaga() {
  while (true) {
    const { payload } = yield take(setWebasto.type);

    yield put({
      type: setTooltip.type,
      payload: { type: toggleWebasto.started.type, on: payload.on },
    });

    try {
      yield call(setWebastoWorker, {
        method: 'POST',
        url: `/api/v3/Devices/${payload.deviceId}/Webasto`,
        body: JSON.stringify({
          on: payload.on,
        }),
      });
    } catch (e) {
      yield put({
        type: setTooltip.type,
        payload: { type: toggleWebasto.failed.type, on: payload.on },
      });
    }
  }
}
function* webastoPeriodSaga() {
  while (true) {
    const { payload } = yield take(setWebastoPeriodClick.type);

    try {
      yield call(setWebastoPeriodInSecondsWorker, {
        method: 'POST',
        url: `/api/v3/Devices/${payload.deviceId}/webastoPeriodInSeconds`,
        body: JSON.stringify({
          delay: payload.seconds,
        }),
      });
    } catch (e) {
      void 0;
    }

    yield delay(1000);
  }
}



// WEBPACK FOOTER //
// ./src/sagas/devices/index.js